import type { PvpSeat } from '@kenocyber/shared';
import type { PvpClientState, PvpIntent } from './connection';
/*
 * `holdsHunt` and `arenaIsOpen` are deliberately NOT imported here.
 *
 * They answer "what does this state imply", and this object answers "what did I
 * actually take" - and the difference was a real defect: a connection that
 * failed past the grace window moves the machine to `error`, which implies no
 * hold, so leaving from there released nothing and left the hunt hidden for
 * good. Releasing follows `owned`, and the seam is kept unimportable so it
 * cannot quietly come back.
 */
import {
  matchOf,
  PVP_INITIAL_STATE,
  type PvpMatchIdentity,
  type PvpUiError,
  type PvpUiEvent,
  type PvpUiState,
  pvpReducer,
} from './machine';

/**
 * Everything the duel's interface DOES, with no React and no I/O in it.
 *
 * ## Why this is not a hook
 *
 * A hook cannot be asked "what happens if the challenge answers after the
 * player left, while the tab is hidden, and then the bootstrap fails twice".
 * A plain object can, and every rule worth having here is one of those: a
 * transition that must not leave the hunt hidden, a lease that must not be
 * released twice, a socket that must not repaint a duel it was replaced in.
 * The hook that wraps this is a subscription and a cleanup, and nothing else.
 *
 * ## Resources are OWNED, not inferred
 *
 * The fields under `owned` are the whole answer to "what does this thing hold
 * right now": one operation, one connection, one generation, one pause lease,
 * whether the arena is open, whether the hunt is hidden, and which recovery is
 * running. Inferring any of them from the reducer's state would mean two
 * sources of truth about a thing that must be released exactly once.
 *
 * ## The transition between modes is a transaction
 *
 * Taking the hunt down and putting the duel up is several steps, and a failure
 * halfway through is what leaves a player looking at nothing. So the entry
 * records exactly which steps ran and the rollback undoes exactly those - it
 * never closes an arena that was not opened, and never releases a lease twice.
 */

/* -------------------------------------------------------------------------- */
/*  Dependencies                                                              */
/* -------------------------------------------------------------------------- */

/** The one-use capability and where its socket lives. Never stored anywhere. */
export interface PvpControllerConnection {
  connect(matchId: string): void;
  declareReady(): void;
  sendIntent(intent: PvpIntent): number | null;
  /**
   * Tells the room the player chose to abandon (ADR 0043).
   *
   * Answers whether the frame was actually written. It is the one call here
   * whose answer matters: a player must never be told they left a match the room
   * never heard about.
   */
  requestLeave(): boolean;
  dispose(): void;
  /**
   * Os cortes confirmados desde a chamada anterior, e esvazia a fila.
   *
   * A ligacao e quem deduplica - por id de projetil, e a memoria morre com a
   * partida - portanto este e o unico leitor legitimo.
   */
  drainProcs(): readonly {
    readonly i: number;
    readonly e: string;
    /* Quem foi curado, e o tique em que o facto nasceu. */
    readonly p: string;
    /* Em quem o efeito foi confirmado. Autoritativo (gate V28G.U). */
    readonly q: string;
    readonly t: number;
    /* A vida realmente acrescentada, em milli. Ausente se nao cura. */
    /** A cura, em MILLI e como string decimal (gate V28G.S.1). */
    readonly h?: string | undefined;
    readonly x: number;
    readonly y: number;
  }[];
  subscribe(listener: (state: PvpClientState) => void): () => void;
  /** As detonações de sangramento desde a chamada anterior (gate V28G.N). */
  drainBleedBursts(): readonly {
    readonly key: string;
    readonly p: string;
    readonly q: string;
    readonly n: number;
    /** O dano aplicado, em MILLI (gate V28G.S.1). */
    readonly d: bigint;
  }[];
  /** As reflexões confirmadas desde a chamada anterior (gate V28G.O). */
  drainReflects(): readonly {
    readonly key: string;
    readonly p: string;
    readonly q: string;
    readonly k: string;
    readonly s: 'i' | 'l';
    readonly x: number;
    readonly y: number;
    readonly tx: number;
    readonly ty: number;
    /** O dano aplicado, em MILLI, ou `null` numa interceção (V28G.S.1). */
    readonly d: bigint | null;
  }[];
  /** As explosoes confirmadas desde a chamada anterior (gate V28G.J). */
  drainBlasts(): readonly {
    readonly i: number;
    readonly e: string;
    readonly p: string;
    readonly t: number;
    readonly x: number;
    readonly y: number;
    readonly r: number;
    readonly q: readonly string[];
  }[];
}

export interface PvpControllerDeps {
  /** `POST /api/pvp/challenge`. Returns the match and its code. */
  readonly createChallenge: (signal: AbortSignal) => Promise<{
    matchId: string;
    code: string;
    seat: PvpSeat;
  }>;
  /**
   * Remembers, for THIS TAB, a duel the player chose to leave.
   *
   * ## Why this exists and why it is the only thing stored
   *
   * The room cannot tell a reload from a walk-out: both are a socket that
   * closed, and it holds the seat for twelve seconds either way. That is
   * correct - a player whose connection died deserves those seconds. But it
   * means the discovery read still finds the match for a few seconds after
   * somebody pressed SAIR, and a reload in that window would put them back
   * into a duel they had just left.
   *
   * So the intention is remembered where the intention was expressed: in
   * the tab, for the length of the tab. It is a match id and nothing else -
   * no ticket, no cookie, no seat, no health, no result - and it can only
   * ever REFUSE a resume. Losing it costs a player one unwanted rejoin;
   * forging it costs an attacker nothing they did not already have, since
   * they could simply press the button.
   */
  readonly leftMatches: {
    remember(matchId: string): void;
    was(matchId: string): boolean;
  };
  /**
   * `GET /api/pvp/active`. Whether this account is already in a duel.
   *
   * The authoritative answer to a question a reloaded tab cannot answer for
   * itself. Nothing is stored in the browser to make this work: the rows
   * that already refuse a second challenge are the ones that answer it.
   */
  readonly findActiveMatch: (
    signal: AbortSignal,
  ) => Promise<{ matchId: string; seat: PvpSeat; state: string } | null>;
  /** `POST /api/pvp/accept`. */
  readonly acceptChallenge: (
    code: string,
    signal: AbortSignal,
  ) => Promise<{ matchId: string; seat: PvpSeat }>;
  /** Builds a connection. One per generation; the old one is disposed first. */
  readonly openConnection: () => PvpControllerConnection;

  /* -- presentation -- */

  /** Takes a pause lease. The release it returns is idempotent. */
  readonly acquirePause: () => () => void;
  /** `hunt:visible`. */
  readonly setHuntVisible: (visible: boolean) => void;
  /** Zeroes whatever the hunt thinks is held down, before the duel takes over. */
  readonly clearHuntIntent: () => void;
  readonly openArena: (seat: PvpSeat) => void;
  readonly publishAuthoritative: (state: PvpClientState) => void;
  /**
   * Os cortes CONFIRMADOS desde a publicacao anterior.
   *
   * Separado do estado de proposito: o estado descreve onde tudo esta e e
   * republicado sempre; isto e uma lista de FACTOS, entregue uma vez. Colar um
   * acontecimento ao estado fa-lo-ia ser redesenhado a cada republicacao.
   */
  readonly publishProcs: (
    cuts: readonly {
      readonly id: number;
      readonly procId: string;
      /* Quem foi curado, e quando: a chave visual e `(atacante, tique)`. */
      readonly attacker: string;
      /*
       * Em QUEM o efeito foi confirmado, e é autoritativo (gate V28G.U).
       *
       * Deixou de ser deitado fora porque passou a decidir alguma coisa: no
       * Roubo de Vida o curado é o `attacker` e o alvo não muda decisão nenhuma;
       * numa cura de suporte o sentido inverte-se, e é ESTE id que diz sobre que
       * nave o número aparece.
       */
      readonly target: string;
      readonly tick: number;
      /* A vida realmente acrescentada, em milli. Ausente se nao cura. */
      readonly healedMilli?: number | undefined;
      readonly x: number;
      readonly y: number;
    }[],
  ) => void;
  /**
   * As EXPLOSÕES confirmadas desde a publicação anterior (gate V28G.J).
   *
   * Separadas de `publishProcs` porque são outra coisa: um proc tem um alvo, e
   * uma explosão tem um centro, um raio e uma lista de quem levou. Uma bomba que
   * detona contra uma parede não atingiu ninguém, e não haveria alvo que pôr no
   * primeiro.
   */
  readonly publishBlasts: (
    blasts: readonly {
      readonly id: number;
      readonly effectId: string;
      readonly attacker: string;
      readonly tick: number;
      readonly x: number;
      readonly y: number;
      /* O raio MECÂNICO, em unidades de mundo. A cena desenha este. */
      readonly radius: number;
      /* Quem levou dano. Nunca quem estava por perto. */
      readonly hit: readonly string[];
    }[],
  ) => void;
  /**
   * As DETONAÇÕES de sangramento confirmadas desde a publicação anterior
   * (gate V28G.N).
   *
   * Separadas das duas de cima porque o facto tem outra forma: uma detonação não
   * tem projétil e não tem centro nem raio - ela acontece SOBRE um casco, por
   * ordem de um autor, com uma contagem de marcas. A chave já vem pronta e
   * deduplicada pela ligação.
   */
  readonly publishBleedBursts: (
    bursts: readonly {
      readonly key: string;
      readonly attacker: string;
      readonly target: string;
      readonly marks: number;
      /* O dano realmente aplicado. A cena imprime-o; nunca o recalcula. */
      /** O dano aplicado, em MILLI. A cena formata-o (gate V28G.S.1). */
      readonly damage: number;
    }[],
  ) => void;
  /**
   * As REFLEXÕES confirmadas, entregues UMA vez (gate V28G.O).
   *
   * Separadas das três de cima porque o facto tem outra forma: uma reflexão
   * acontece ENTRE dois cascos, e carrega as DUAS posições do tique em que
   * aconteceu. A chave já vem pronta e deduplicada pela ligação.
   */
  readonly publishReflects: (
    reflects: readonly {
      readonly key: string;
      readonly reflector: string;
      readonly target: string;
      readonly payload: string;
      readonly stage: 'i' | 'l';
      readonly x: number;
      readonly y: number;
      readonly tx: number;
      readonly ty: number;
      /* O dano realmente aplicado, ou `null` numa interceção sem dano ainda. */
      /** O dano aplicado, em MILLI, ou `null` numa interceção. */
      readonly damage: number | null;
    }[],
  ) => void;
  readonly closeArena: () => void;
  /** Tells the scene which sequence an intent was sent under. Presentation. */
  readonly intentSent: (seq: number, intent: PvpIntent) => void;

  /* -- the way back -- */

  /**
   * The hunt's own authoritative bootstrap, through the existing path.
   *
   * The controller never restores a snapshot it kept: a hunt that has been
   * away for a duel has to be re-authorised, and the only honest source for
   * that is the server.
   */
  readonly bootstrapHunt: (signal: AbortSignal) => Promise<void>;
}

/* -------------------------------------------------------------------------- */
/*  What the controller holds                                                 */
/* -------------------------------------------------------------------------- */

interface Owned {
  /** The only operation whose answer is still wanted. */
  operation: number;
  abort: AbortController | null;
  connection: PvpControllerConnection | null;
  unsubscribe: (() => void) | null;
  generation: number;
  /** Non-null exactly while the duel is holding the hunt paused. */
  pauseLease: (() => void) | null;
  arenaOpened: boolean;
  huntHidden: boolean;
  recovery: number;
  disposed: boolean;
  /** Set once the room has been seen at `ready`, so entry runs exactly once. */
  entered: boolean;
}

export interface PvpController {
  subscribe(listener: (state: PvpUiState) => void): () => void;
  getState(): PvpUiState;
  open(): void;
  createMatch(): void;
  joinMatch(code: string): void;
  markReady(): void;
  /** Clears a refusal that no longer describes the code being typed. */
  codeEdited(): void;
  /**
   * Asks the server whether this account is mid-duel, and rejoins if it is.
   *
   * Called once when the app mounts. A reload is indistinguishable from a
   * dropped socket as far as the room is concerned - it holds the seat for
   * twelve seconds either way - so the only thing missing was somebody
   * coming back to claim it.
   */
  resume(): void;
  /** Carries one coalesced intent to the room. Only while it is running. */
  sendIntent(intent: PvpIntent): void;
  leave(): void;
  retryHuntRecovery(): void;
  dispose(): void;
}

export function createPvpController(deps: PvpControllerDeps): PvpController {
  let state: PvpUiState = PVP_INITIAL_STATE;
  const listeners = new Set<(state: PvpUiState) => void>();
  const owned: Owned = {
    operation: 0,
    abort: null,
    connection: null,
    unsubscribe: null,
    generation: 0,
    pauseLease: null,
    arenaOpened: false,
    huntHidden: false,
    recovery: 0,
    disposed: false,
    entered: false,
  };

  function dispatch(event: PvpUiEvent): void {
    const next = pvpReducer(state, event);
    if (next === state) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener(state);
    }
  }

  /** A fresh id, which also invalidates every answer still in flight. */
  function nextOperation(): number {
    owned.abort?.abort();
    owned.operation += 1;
    owned.abort = new AbortController();
    return owned.operation;
  }

  /**
   * Whether an answer is still wanted.
   *
   * `AbortController` is help, not proof: a promise can resolve after an abort,
   * and a test double frequently does. The id is what decides.
   */
  const current = (operation: number): boolean => !owned.disposed && owned.operation === operation;

  /* -------------------------------------------------------- connection -- */

  function dropConnection(): void {
    owned.unsubscribe?.();
    owned.unsubscribe = null;
    owned.connection?.dispose();
    owned.connection = null;
  }

  function connect(match: PvpMatchIdentity): void {
    dropConnection();
    owned.generation += 1;
    const generation = owned.generation;
    const connection = deps.openConnection();
    owned.connection = connection;
    owned.entered = false;

    owned.unsubscribe = connection.subscribe((client) => {
      /*
       * Everything a replaced socket says is dropped here.
       *
       * The room applies the same rule to a superseded seat; applying it on
       * this side too is what stops an old generation repainting a live duel,
       * and it is checked before the reducer rather than inside it so nothing
       * reaches the arena either.
       */
      if (owned.disposed || generation !== owned.generation) {
        return;
      }
      onClientState(generation, match, client);
    });
    connection.connect(match.matchId);
  }

  function onClientState(
    generation: number,
    match: PvpMatchIdentity,
    client: PvpClientState,
  ): void {
    if (client.failure) {
      dispatch({
        type: 'CONNECTION_FAILED',
        generation,
        failure: client.failure,
        detail: client.failureDetail,
      });
      return;
    }

    /*
     * The room is complete, or was already fighting when this socket arrived.
     *
     * This is the moment the dedicated transition runs, and `entered` is what
     * makes it run exactly once - a second frame must not take the hunt down
     * twice.
     *
     * `running` belongs here as much as `ready` does. A client REJOINING after
     * a reload is welcomed into a match that is already under way and will
     * never be told `ready` again, so a condition that named only that state
     * left the panel sitting on RECONNECTING for ever with a live socket
     * behind it - measured in a browser, with the room perfectly happy and the
     * seat still held.
     */
    const live = client.matchState === 'ready' || client.matchState === 'running';
    if (!owned.entered && live && client.seat !== null) {
      owned.entered = true;
      enterPvp(generation, { ...match, seat: client.seat }, client);
      return;
    }

    if (client.result) {
      dispatch({ type: 'ENDED', generation, result: client.result });
      publish(client);
      return;
    }
    dispatch({ type: 'AUTHORITATIVE_STATE', generation, state: client });
    publish(client);
  }

  /** Only ever from the generation that owns the arena. */
  function publish(client: PvpClientState): void {
    if (owned.arenaOpened) {
      deps.publishAuthoritative(client);
      publishDrainedProcs();
      publishDrainedBlasts();
      publishDrainedBleedBursts();
      publishDrainedReflects();
    }
  }

  /**
   * Drena a fila da ligacao e publica o que estiver la.
   *
   * Drenada aqui e nao na cena: a ligacao e quem deduplica, e uma cena que
   * lesse a fila diretamente seria uma segunda leitora do mesmo facto.
   * Silencioso quando nao ha nada, que e o caso esmagadoramente comum.
   */
  function publishDrainedProcs(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const cuts = connection.drainProcs();
    if (cuts.length === 0) {
      return;
    }
    deps.publishProcs(
      cuts.map((cut) => ({
        id: cut.i,
        procId: cut.e,
        attacker: cut.p,
        target: cut.q,
        tick: cut.t,
        /*
         * O número FLUTUANTE da cura, e é o único sítio onde um milli de vida
         * atravessa para `number` (gate V28G.S.1).
         *
         * Ele não decide nada: `healNumbersFrom` soma-o e `formatHealAmount`
         * escreve-o, e acima de mil pontos esse formatador já abrevia para a
         * escala curta. A vida que DECIDE - a barra, o teto, a instantânea, o
         * quadro - continua em `bigint` de ponta a ponta e nunca passa por aqui.
         */
        ...(cut.h === undefined ? {} : { healedMilli: Number(cut.h) }),
        x: cut.x,
        y: cut.y,
      })),
    );
  }

  /** A gémea de cima, para as DETONAÇÕES de sangramento (gate V28G.N). */
  function publishDrainedBleedBursts(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const bursts = connection.drainBleedBursts();
    if (bursts.length === 0) {
      return;
    }
    deps.publishBleedBursts(
      bursts.map((burst) => ({
        key: burst.key,
        attacker: burst.p,
        target: burst.q,
        marks: burst.n,
        /* Para a APRESENTAÇÃO, em milli. A vida que decide fica em `bigint`. */
        damage: Number(burst.d),
      })),
    );
  }

  /** A gémea de cima, para as REFLEXÕES (gate V28G.O). */
  function publishDrainedReflects(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const reflects = connection.drainReflects();
    if (reflects.length === 0) {
      return;
    }
    deps.publishReflects(
      reflects.map((reflect) => ({
        key: reflect.key,
        reflector: reflect.p,
        target: reflect.q,
        payload: reflect.k,
        stage: reflect.s,
        x: reflect.x,
        y: reflect.y,
        tx: reflect.tx,
        ty: reflect.ty,
        damage: reflect.d === null ? null : Number(reflect.d),
      })),
    );
  }

  /** A gémea de cima, para as explosões (gate V28G.J). */
  function publishDrainedBlasts(): void {
    const connection = owned.connection;
    if (!connection) {
      return;
    }
    const blasts = connection.drainBlasts();
    if (blasts.length === 0) {
      return;
    }
    deps.publishBlasts(
      blasts.map((blast) => ({
        id: blast.i,
        effectId: blast.e,
        attacker: blast.p,
        tick: blast.t,
        x: blast.x,
        y: blast.y,
        radius: blast.r,
        hit: [...blast.q],
      })),
    );
  }

  /* ------------------------------------------------------- transaction -- */

  /**
   * Takes the hunt down and puts the duel up, recording every step.
   *
   * Synchronous from the first step to the last on purpose: an `await` between
   * hiding the hunt and opening the arena is a frame in which the browser can
   * paint neither, and the player sees a blank game.
   */
  function enterPvp(generation: number, match: PvpMatchIdentity, client: PvpClientState): void {
    dispatch({ type: 'ENTER_STARTED', generation, match });
    const done = { lease: false, hidden: false, arena: false };
    try {
      deps.clearHuntIntent();
      owned.pauseLease = deps.acquirePause();
      done.lease = true;

      deps.setHuntVisible(false);
      owned.huntHidden = true;
      done.hidden = true;

      deps.openArena(match.seat);
      owned.arenaOpened = true;
      done.arena = true;

      deps.publishAuthoritative(client);
      publishDrainedProcs();
      publishDrainedBlasts();
      publishDrainedBleedBursts();
      publishDrainedReflects();
      dispatch({ type: 'ENTERED', generation, state: client });
    } catch (error) {
      rollbackEntry(done);
      dispatch({
        type: 'ENTER_FAILED',
        generation,
        error: { reason: safeReason(error), recoverable: true },
      });
    }
  }

  /**
   * Undoes exactly the steps that ran, and not one more.
   *
   * The arena is closed only if it was opened - `arenaIsOpen` answers false for
   * `entering_pvp` for this reason - and the lease is released through the
   * idempotent handle rather than by clearing a flag somebody else also reads.
   */
  function rollbackEntry(done: { lease: boolean; hidden: boolean; arena: boolean }): void {
    if (done.arena) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    if (done.hidden) {
      deps.setHuntVisible(true);
      owned.huntHidden = false;
    }
    if (done.lease) {
      releaseLease();
    }
  }

  function releaseLease(): void {
    owned.pauseLease?.();
    owned.pauseLease = null;
  }

  /* ------------------------------------------------------------- entry -- */

  function open(): void {
    dispatch({ type: 'OPEN' });
  }

  /**
   * The player is typing a different code, so the last refusal stops applying.
   *
   * Deliberately not "dismiss the error": nobody dismisses anything. The
   * message goes when the thing it was about goes, which is the moment the
   * code changes.
   */
  function codeEdited(): void {
    dispatch({ type: 'CODE_EDITED' });
  }

  function resume(): void {
    /*
     * Only from a closed panel, which is where a freshly loaded page is.
     *
     * A player who has already opened the panel and is typing a code is
     * telling us what they want, and an answer that arrives afterwards must
     * not take the screen away from them.
     */
    if (state.kind !== 'closed') {
      return;
    }
    /*
     * The panel is NOT opened before the answer arrives.
     *
     * Opening first was the obvious order and it is wrong: every page load,
     * for every account, would put the duel panel over the hunt and then take
     * it away again. The panel opens only when there is something to open it
     * for.
     */
    const operation = nextOperation();
    const signal = owned.abort?.signal as AbortSignal;
    /*
     * Announced BEFORE the request, so the shell knows an arena may be coming.
     *
     * This opens nothing - `restoring` has no panel, no lobby and no arena. It
     * exists so the hunt's own overlays can stand aside for an arena that is one
     * round trip away, which is the flash gate V24D.2 forbids.
     */
    dispatch({ type: 'RESUME_STARTED' });
    deps
      .findActiveMatch(signal)
      .then((active) => {
        if (
          !active ||
          owned.disposed ||
          state.kind !== 'restoring' ||
          operation !== owned.operation
        ) {
          // Nothing to restore, or somebody else now owns the screen. Either
          // way the discovery is over and must not keep the hunt waiting.
          dispatch({ type: 'RESUME_ABSENT' });
          return;
        }
        if (deps.leftMatches.was(active.matchId)) {
          /*
           * This tab left this duel on purpose. The room is still holding
           * the seat because the grace has not expired, and that is not a
           * reason to drag somebody back into a match they walked out of.
           */
          dispatch({ type: 'RESUME_ABSENT' });
          return;
        }
        dispatch({ type: 'OPEN' });
        const match: PvpMatchIdentity = {
          matchId: active.matchId,
          code: null,
          seat: active.seat,
        };
        dispatch({ type: 'RESUME_FOUND', operation: owned.operation, match });
        connect(match);
      })
      .catch(() => {
        /*
         * A discovery that fails changes nothing - except that it is over.
         *
         * It is a hint, not an authorisation: the player can still open the
         * panel and challenge somebody, and the room refuses a second duel by
         * itself if there really was one. What must not happen is the hunt's
         * overlays waiting for ever on an answer that is never coming, so the
         * discovery is closed here exactly as it is on a negative answer.
         */
        dispatch({ type: 'RESUME_ABSENT' });
      });
  }

  function createMatch(): void {
    if (state.kind !== 'choosing') {
      return;
    }
    const operation = nextOperation();
    dispatch({ type: 'CREATE_REQUESTED', operation });
    const signal = owned.abort?.signal as AbortSignal;
    deps
      .createChallenge(signal)
      .then((made) => {
        if (!current(operation)) {
          return;
        }
        const match: PvpMatchIdentity = {
          matchId: made.matchId,
          code: made.code,
          seat: made.seat,
        };
        dispatch({ type: 'CREATE_SUCCEEDED', operation, match, code: made.code });
        // The socket opens now: the room reports the second seat arriving, and
        // that report is what starts the dedicated transition.
        connect(match);
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        dispatch({
          type: 'CREATE_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function joinMatch(code: string): void {
    if (state.kind !== 'choosing') {
      return;
    }
    const normalised = code.trim().toUpperCase();
    const operation = nextOperation();
    dispatch({ type: 'JOIN_REQUESTED', operation });
    const signal = owned.abort?.signal as AbortSignal;
    deps
      .acceptChallenge(normalised, signal)
      .then((joined) => {
        if (!current(operation)) {
          return;
        }
        const match: PvpMatchIdentity = {
          matchId: joined.matchId,
          code: null,
          seat: joined.seat,
        };
        dispatch({ type: 'JOIN_SUCCEEDED', operation, match });
        connect(match);
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        dispatch({
          type: 'JOIN_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function markReady(): void {
    if (state.kind !== 'ready' || state.declared) {
      // One per generation. A second click is not a second player, and the
      // server confirms readiness - sending it is not assuming it.
      return;
    }
    dispatch({ type: 'READY_REQUESTED', generation: state.generation });
    owned.connection?.declareReady();
  }

  /**
   * What the player is doing, on its way to the room.
   *
   * The scene captures WASD, the mouse and the trigger, coalesces them to the
   * protocol's cadence and publishes ONE intent. This is the only thing that
   * carries it to the socket, and until two real browsers tried to duel there
   * was nothing here at all: the scene published, nobody listened, and a duel
   * was two ships that could not move.
   *
   * Guarded on `running` rather than on the connection alone. An intent sent
   * while the room is still `ready` belongs to a match that has not started;
   * the connection drops it too, and refusing here as well keeps the rule where
   * the rest of the state machine's rules live.
   */
  function sendIntent(intent: PvpIntent): void {
    if (state.kind !== 'running') {
      return;
    }
    const seq = owned.connection?.sendIntent(intent);
    if (typeof seq === 'number') {
      /*
       * Told to the scene, which replays every unacknowledged intent to draw
       * the local ship without waiting 50 ms for the room to answer. Only a
       * command that was actually SENT gets a sequence, so a prediction can
       * never include one the room will never process.
       */
      deps.intentSent(seq, intent);
    }
  }

  /* -------------------------------------------------------------- exit -- */

  /**
   * The player chose to abandon a fight that is under way (ADR 0043).
   *
   * ## Why this is not the same thing as the button used to do
   *
   * It used to drop the socket and walk home, which the room could only read as
   * a DISCONNECTION - indistinguishable from an F5, and handled by the grace
   * window that exists to protect somebody who never chose to go. So the duel
   * ended twelve seconds late in the 1x1, and in a 3x3 where everybody pressed it
   * the room never ended at all: with no socket left, nothing drove its loop.
   *
   * ## What happens here, and in this order
   *
   * The intent goes out and NOTHING else. The socket stays open, the arena stays
   * up, the lease stays held and the hunt stays hidden, because the thing that
   * ends a match is the room's `ended` frame arriving on that same socket. The
   * walk home runs afterwards, through the ordinary exit, from the result screen
   * the opponent is looking at too.
   *
   * If the frame could not even be written, the player is still in the fight and
   * is told so. Inventing a result, or leaving anyway, would be the optimistic
   * exit this whole ADR exists to forbid.
   */
  function requestAbandon(): void {
    if (state.kind !== 'running' || state.abandon === 'sending') {
      return;
    }
    const generation = state.generation;
    // Dispatched BEFORE the write, so a second click in the same frame finds the
    // state already busy rather than racing it.
    dispatch({ type: 'ABANDON_REQUESTED', generation });

    const connection = owned.connection;
    if (!connection || owned.generation !== generation || !connection.requestLeave()) {
      dispatch({ type: 'ABANDON_FAILED', generation });
    }
  }

  function leave(): void {
    if (state.kind === 'closed' || state.kind === 'leaving') {
      return;
    }
    /*
     * A fight is abandoned, never walked out of.
     *
     * Every other state keeps the exit it always had: a lobby is a panel to
     * close, a `reconnecting` socket has already gone and the room is holding
     * the seat under its grace, and a `completed` match is over - going back to
     * the hunt IS leaving it.
     */
    if (state.kind === 'running') {
      requestAbandon();
      return;
    }
    /*
     * What this controller actually OWNS, not what the state implies.
     *
     * Reading `holdsHunt(state)` here was a real defect, found by driving a
     * real socket through a real controller: a connection that fails past the
     * grace window moves the machine to `error`, and `error` does not hold the
     * hunt - so leaving from there released nothing and left the hunt hidden
     * and paused for good, with no way back.
     *
     * The lease and the arena are resources, and a resource is released
     * according to whether it was taken. That is the principle this object is
     * built on, and these two lines were the place it was not being followed.
     */
    /*
     * Remembered BEFORE anything is torn down, because the identity is about
     * to be dropped along with the state that holds it.
     */
    const leaving = matchOf(state);
    if (leaving) {
      deps.leftMatches.remember(leaving.matchId);
    }

    const wasHolding = owned.pauseLease !== null || owned.huntHidden;
    const wasOpen = owned.arenaOpened;

    // Whatever was in flight is no longer wanted, whichever road we take.
    nextOperation();
    dispatch({ type: 'LEAVE_REQUESTED' });
    dropConnection();

    if (!wasHolding) {
      /*
       * The lobby, an invite nobody took, a socket that never filled a room.
       * Nothing was taken from the hunt, so there is no lease to release, no
       * visibility to restore and no bootstrap to ask for.
       */
      return;
    }
    if (wasOpen) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    // The hunt stays hidden and paused until the server has re-authorised it.
    startRecovery();
  }

  function startRecovery(): void {
    const operation = nextOperation();
    owned.recovery = operation;
    dispatch({ type: 'HUNT_RECOVERY_STARTED', operation });
    const signal = owned.abort?.signal as AbortSignal;
    deps
      .bootstrapHunt(signal)
      .then(() => {
        if (!current(operation)) {
          return;
        }
        /*
         * Adopted FIRST, and only then shown.
         *
         * Restoring visibility before the answer lands would put a hunt on
         * screen that nobody has re-authorised - the exact frame of stale game
         * this whole sequence exists to avoid.
         */
        deps.setHuntVisible(true);
        owned.huntHidden = false;
        releaseLease();
        dispatch({ type: 'HUNT_RECOVERED', operation });
      })
      .catch((error: unknown) => {
        if (!current(operation)) {
          return;
        }
        // Stays in recovery, holding the hunt hidden and paused, offering a
        // retry. Showing it here would show a hunt out of date by a duel.
        dispatch({
          type: 'HUNT_RECOVERY_FAILED',
          operation,
          error: { reason: safeReason(error), recoverable: true },
        });
      });
  }

  function retryHuntRecovery(): void {
    if (state.kind !== 'recovering_hunt') {
      return;
    }
    // A NEW operation id, so the attempt that already failed cannot resolve
    // this one.
    startRecovery();
  }

  /* ----------------------------------------------------------- dispose -- */

  /**
   * Lets go of everything, once.
   *
   * React's cleanup cannot await a bootstrap, so this does NOT try to
   * re-authorise the hunt on the way out. What it must never do is leave the
   * hunt permanently invisible: the visibility is restored fail-safe here, and
   * the next mount runs its own bootstrap through the ordinary path. No
   * snapshot is kept and none is restored.
   */
  function dispose(): void {
    if (owned.disposed) {
      return;
    }
    owned.disposed = true;
    owned.abort?.abort();
    owned.abort = null;
    dropConnection();

    if (owned.arenaOpened) {
      deps.closeArena();
      owned.arenaOpened = false;
    }
    if (owned.huntHidden) {
      deps.setHuntVisible(true);
      owned.huntHidden = false;
    }
    releaseLease();
    listeners.clear();
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
    getState: () => state,
    open,
    createMatch,
    joinMatch,
    codeEdited,
    resume,
    markReady,
    sendIntent,
    leave,
    retryHuntRecovery,
    dispose,
  };
}

/**
 * A reason a player can be shown.
 *
 * The API's machine reason when there is one, and a generic string otherwise.
 * Never a stack, never a ticket, never an id: an error message is not a place
 * to put something a screenshot would then carry.
 */
function safeReason(error: unknown): string {
  const reason = (error as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? reason : 'unavailable';
}

/** What the reducer's error carries, re-exported for the UI's convenience. */
export type { PvpUiError };
